import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { getErpUserId } from '../auth/erp-auth-token.util';
import { resolveErpStationIdForApi } from '../erp-api-query.helper';
import { WarehouseService } from '../warehouse/warehouse.service';
import { buildErpEntrepotPayloadFromLcForm } from '../warehouse/erp-lc-entrepot-payload.util';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';
import { AccesLogicielCaisseService } from './acces-logiciel-caisse.service';
import { UsersCoordinationService } from './users-coordination.service';

export interface LogicielCaisseEntrepotOption {
  id: string;
  label: string;
}

export interface EntrepotRow {
  id: number;
  libelle?: string | null;
  user_id?: number;
  station_id?: number;
  is_reassort?: boolean | number | null;
  is_globale?: boolean | number | null;
}

/** Ligne tableau « Gestion des entrepôts » (ERP). */
export interface EntrepotTableRow {
  id: string;
  'CODE ENTREPÔT': string;
  LIBELLÉ: string;
  VILLE: string;
  PAYS: string;
  /** Ordre réassort (1, 2, …) si défini en base ; sinon « — ». */
  'PRIORITÉ'?: string;
  is_reassort?: boolean;
  is_globale?: boolean;
}

export interface EntrepotFormPayload {
  libelle: string;
  ville: string;
  pays: string;
  code_entrepot: string | null;
  is_reassort: boolean;
  is_globale: boolean;
}

export interface EntrepotCaisseOption {
  id: number;
  numero_de_caisse: number;
  entrepot_id: number;
  entrepot_libelle?: string;
  uuid?: string | null;
  has_uuid?: boolean;
  label?: string;
}

/** Entrepôt renvoyé après création / mise à jour (API). */
export interface EntrepotApiRecord {
  id: number;
  libelle?: string | null;
  ville?: string | null;
  pays?: string | null;
  code_entrepot?: string | null;
  station_id?: number | null;
  user_id?: number | null;
  is_reassort?: boolean | number | null;
  is_globale?: boolean | number | null;
  /** Id erp_main.entrepots après POST stock ERP. */
  erp_id?: number | null;
}

interface EntrepotMutationResponse {
  success: boolean;
  entrepot?: Record<string, unknown>;
  error?: string;
}

interface EntrepotsListResponse {
  success: boolean;
  entrepots?: EntrepotRow[];
  error?: string;
}

interface EntrepotsErpListResponse {
  success: boolean;
  items?: EntrepotTableRow[];
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseEntrepotService {
  private entrepotOptionsCache$: Observable<LogicielCaisseEntrepotOption[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService,
    private readonly accesService: AccesLogicielCaisseService,
    private readonly coordinationService: UsersCoordinationService,
    private readonly warehouseService: WarehouseService
  ) {}

  /** GET /api/entrepots/entrepot/:station_id */
  getByStation(): Observable<EntrepotRow[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${stationId}`;
    return this.http.get<EntrepotsListResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.entrepots)) {
          throw new Error(res?.error || 'Réponse entrepôts invalide');
        }
        return res.entrepots;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des entrepôts.'))
      )
    );
  }

  /**
   * GET /api/entrepots/entrepot/all?user_id=&station_id=
   * Liste publique (sans JWT) — filtre strict user + station (backend-logiciel-caisse).
   */
  getByUserAndStation(userId: number, stationId: number): Observable<EntrepotRow[]> {
    const uid = Number(userId);
    const sid = Number(stationId);
    if (!Number.isFinite(uid) || uid <= 0 || !Number.isFinite(sid) || sid <= 0) {
      console.error('[LC Entrepot][getByUserAndStation] IDs invalides', { userId, stationId });
      return throwError(() => new Error('user_id et station_id (entiers positifs) sont requis'));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/all`;
    const params = { user_id: String(uid), station_id: String(sid) };
    console.log('[LC Entrepot][getByUserAndStation] GET', url, params);
    return this.http.get<EntrepotsListResponse>(url, { params }).pipe(
      map((res) => {
        console.log('[LC Entrepot][getByUserAndStation] réponse brute', {
          success: res?.success,
          count: Array.isArray(res?.entrepots) ? res.entrepots.length : null,
          entrepots: res?.entrepots,
          error: res?.error,
        });
        if (!res?.success || !Array.isArray(res.entrepots)) {
          throw new Error(res?.error || 'Réponse entrepôts invalide');
        }
        return res.entrepots;
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('[LC Entrepot][getByUserAndStation] erreur HTTP', err?.status, err?.error || err);
        return throwError(() => lcHttpError(err, 'Erreur lors du chargement des entrepôts.'));
      })
    );
  }

  /**
   * Fiche entrepôt LC dans le contexte user + station (PV d'écart, etc.).
   * Primaire : GET /entrepot/all?user_id&station_id puis filtre par id.
   * Repli : GET /entrepot/id/:id (JWT).
   */
  getEntrepotByUserStationAndId(
    userId: number,
    stationId: number,
    entrepotId: number
  ): Observable<EntrepotApiRecord> {
    const uid = Math.trunc(Number(userId));
    const sid = Math.trunc(Number(stationId));
    const eid = Math.trunc(Number(entrepotId));
    if (!Number.isFinite(uid) || uid <= 0 || !Number.isFinite(sid) || sid <= 0 || !Number.isFinite(eid) || eid <= 0) {
      return throwError(() => new Error('user_id, station_id et entrepot_id (entiers positifs) sont requis.'));
    }
    return this.getByUserAndStation(uid, sid).pipe(
      switchMap((rows) => {
        const row = (rows || []).find((r) => Number(r.id) === eid);
        if (row) {
          return of(row as unknown as EntrepotApiRecord);
        }
        return throwError(
          () => new Error(`Entrepôt ${eid} introuvable pour user_id=${uid}, station_id=${sid}.`)
        );
      }),
      catchError((listErr) =>
        this.getEntrepotById(eid).pipe(
          catchError(() =>
            throwError(() =>
              listErr instanceof Error
                ? listErr
                : new Error(String(listErr ?? 'Entrepôt introuvable.'))
            )
          )
        )
      )
    );
  }

  /**
   * Liste pour le ng-select document : backend-logiciel-caisse (user_id + station_id).
   * Format aligné sur bindLabel="nom".
   */
  listForDocumentSelectByUserAndStation(
    userId: number,
    stationId: number
  ): Observable<
    Array<{
      id: number;
      nom: string;
      libelle: string;
      ville: string;
      pays: string;
      adresse: string;
      code_entrepot: string | null;
    }>
  > {
    return this.getByUserAndStation(userId, stationId).pipe(
      map((rows) => this.mapEntrepotRowsToSelect(rows))
    );
  }

  /**
   * Tous les entrepôts de la station LC (JWT) — pour BON TRANSFERT
   * (Entrepôt Initial / Destination), sans filtre user_id / is_globale.
   * GET /api/entrepots/entrepot/:station_id
   */
  listForDocumentSelectByStation(): Observable<
    Array<{
      id: number;
      nom: string;
      libelle: string;
      ville: string;
      pays: string;
      adresse: string;
      code_entrepot: string | null;
    }>
  > {
    return this.getByStation().pipe(map((rows) => this.mapEntrepotRowsToSelect(rows)));
  }

  /**
   * Entrepôts LC pour mapping ERP (transfert / conversion) sans gateway acces-logiciel-caisse.
   * Utilise la session caisse (JWT) puis repli entrepot/all avec user_id + station_id LC.
   */
  listForLcMappingWithoutErpGateway(): Observable<
    Array<{
      id: number;
      nom: string;
      libelle: string;
      ville: string;
      pays: string;
      adresse: string;
      code_entrepot: string | null;
    }>
  > {
    const fallbackByLcIds = (): Observable<
      Array<{
        id: number;
        nom: string;
        libelle: string;
        ville: string;
        pays: string;
        adresse: string;
        code_entrepot: string | null;
      }>
    > => {
      const uid = this.authService.getBackUserId();
      const sid = this.authService.getBackStationId();
      if (uid && sid) {
        return this.listForDocumentSelectByUserAndStation(uid, sid).pipe(catchError(() => of([])));
      }
      return of([]);
    };

    return this.listForDocumentSelectByStation().pipe(
      switchMap((rows) => (rows.length > 0 ? of(rows) : fallbackByLcIds())),
      catchError(() => fallbackByLcIds())
    );
  }

  /**
   * Entrepôts document via IDs LC résolus (session / cache sync / sync-status).
   * GET /api/entrepots/entrepot/all?user_id=&station_id= (backend-logiciel-caisse uniquement).
   */
  /**
   * Entrepôts document :
   * 1) localStorage ERP → users_coordination/resolve → LC /entrepot/all
   * 2) sinon gateway ERP (sync + upsert coordination)
   */
  listForDocumentSelectFromLcSession(): Observable<
    Array<{
      id: number;
      nom: string;
      libelle: string;
      ville: string;
      pays: string;
      adresse: string;
      code_entrepot: string | null;
    }>
  > {
    const erp = this.authService.getErpContext();
    if (!erp?.erpUserId || !(erp.erpStationId > 0)) {
      console.warn('[LC Entrepot][listForDocumentSelectFromLcSession] contexte ERP absent');
      return of([]);
    }
    console.log('[LC Entrepot][listForDocumentSelectFromLcSession] ERP localStorage', {
      erpUserId: erp.erpUserId,
      erpStationId: erp.erpStationId,
      code_station: erp.code_station,
    });

    return this.coordinationService.resolve(erp.erpUserId, erp.erpStationId).pipe(
      switchMap((coord) => {
        if (coord) {
          console.log('[LC Entrepot] via users_coordination → LC', coord);
          return this.listForDocumentSelectByUserAndStation(
            coord.user_id_logiciel,
            coord.station_id_logiciel
          ).pipe(
            map((rows) => ({ rows, source: 'users_coordination' as const, lc: coord }))
          );
        }
        console.log('[LC Entrepot] pas de coordination → gateway ERP');
        return this.accesService.listEntrepotsForDocument(erp.code_station, erp.erpStationId).pipe(
          map((res) => ({
            rows: this.mapEntrepotRowsToSelect((res.entrepots || []) as unknown as EntrepotRow[]),
            source: 'erp_gateway' as const,
            lc: res.lc,
          })),
          catchError((gatewayErr) => {
            console.warn(
              '[LC Entrepot] gateway entrepots indisponible — repli session LC',
              gatewayErr
            );
            return this.listForLcMappingWithoutErpGateway().pipe(
              map((rows) => ({
                rows,
                source: 'lc_session_fallback' as const,
                lc: null as null,
              }))
            );
          })
        );
      }),
      map((pack) => {
        console.log('[LC Entrepot][listForDocumentSelectFromLcSession] mapped pour ng-select', {
          source: pack.source,
          lc: pack.lc,
          count: pack.rows.length,
          rows: pack.rows,
        });
        return pack.rows;
      }),
      catchError((err) => {
        console.error('[LC Entrepot][listForDocumentSelectFromLcSession] erreur', err);
        return this.listForLcMappingWithoutErpGateway();
      })
    );
  }

  private mapEntrepotRowsToSelect(rows: EntrepotRow[]) {
    return (rows || [])
      .map((row) => {
        const id = Number(row?.id);
        if (!Number.isFinite(id) || id <= 0) {
          return null;
        }
        const r = row as EntrepotRow & {
          ville?: string | null;
          pays?: string | null;
          code_entrepot?: string | null;
          nom?: string | null;
        };
        const nom = String(r.libelle ?? r.nom ?? '').trim() || `Entrepôt ${id}`;
        const ville = String(r.ville ?? '').trim();
        const pays = String(r.pays ?? '').trim();
        return {
          id,
          nom,
          libelle: nom,
          ville,
          pays,
          adresse: [ville, pays].filter(Boolean).join(' • '),
          code_entrepot: r.code_entrepot != null ? String(r.code_entrepot).trim() : null,
          is_globale:
            r.is_globale === true ||
            Number(r.is_globale) === 1,
        };
      })
      .filter((w): w is NonNullable<typeof w> => w != null);
  }

  /** GET /api/entrepots/entrepot/list/station/:station_id (JWT + station) */
  getEntrepotsErpTableRows(): Observable<EntrepotTableRow[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/list/station/${stationId}`;
    return this.http.get<EntrepotsErpListResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items)) {
          throw new Error(res?.error || 'Réponse entrepôts invalide');
        }
        return res.items;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des entrepôts.'))
      )
    );
  }

  /**
   * Tous les entrepôts de la station (sans filtre is_globale) — filtres listes transfert entrepôt.
   */
  getAllEntrepotFilterOptions(): Observable<LogicielCaisseEntrepotOption[]> {
    return this.getEntrepotsErpTableRows().pipe(
      map((items) => this.mapErpTableRowsToSelectOptions(items))
    );
  }

  getEntrepotOptions(): Observable<LogicielCaisseEntrepotOption[]> {
    if (!this.entrepotOptionsCache$) {
      this.entrepotOptionsCache$ = this.getEntrepotsErpTableRows().pipe(
        map((items) =>
          this.mapErpTableRowsToSelectOptions(
            items.filter(
              (row) => row.is_globale === true || Number((row as EntrepotTableRow).is_globale) === 1
            )
          )
        ),
        shareReplay(1)
      );
    }
    return this.entrepotOptionsCache$;
  }

  private mapErpTableRowsToSelectOptions(
    items: EntrepotTableRow[]
  ): LogicielCaisseEntrepotOption[] {
    return (items || [])
      .map((row) => {
        const id = row.id != null ? String(row.id) : '';
        const label =
          row.LIBELLÉ?.trim() ||
          row['CODE ENTREPÔT']?.trim() ||
          (id ? `Entrepôt ${id}` : '');
        return { id, label: label || `Entrepôt ${id}` };
      })
      .filter((o) => o.id !== '')
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }

  clearEntrepotOptionsCache(): void {
    this.entrepotOptionsCache$ = null;
  }

  /**
   * Création double :
   * - ERP stock : POST {stock_url}/api/entrepots (erp_main.entrepots, FK documents)
   * - LC : POST /api/entrepots/entrepot
   */
  createEntrepot(body: EntrepotFormPayload): Observable<EntrepotApiRecord> {
    const erpUserId = getErpUserId();
    const erpStationId = resolveErpStationIdForApi();
    const lcPayload: EntrepotFormPayload = {
      libelle: body.libelle,
      ville: body.ville || '',
      pays: body.pays || '',
      code_entrepot: body.code_entrepot,
      is_reassort: !!body.is_reassort,
      is_globale: !!body.is_globale,
    };

    const erp$ =
      erpUserId != null && erpUserId > 0 && erpStationId > 0
        ? this.warehouseService
            .addWarehouse(
              erpStationId,
              buildErpEntrepotPayloadFromLcForm(lcPayload, erpUserId, erpStationId)
            )
            .pipe(
              map((res) => ({ ok: true as const, res })),
              catchError((err) => of({ ok: false as const, err }))
            )
        : of({ ok: false as const, err: new Error('Session ERP incomplète.') });

    const lc$ = this.createEntrepotLcOnly(lcPayload).pipe(
      map((res) => ({ ok: true as const, res })),
      catchError((err) =>
        of({
          ok: false as const,
          err: err instanceof Error ? err : lcHttpError(err as HttpErrorResponse, 'Erreur LC.'),
        })
      )
    );

    return forkJoin({ erp: erp$, lc: lc$ }).pipe(
      map(({ erp, lc }) => {
        if (!erp.ok && !lc.ok) {
          const erpFail = erp as { ok: false; err: unknown };
          const lcFail = lc as { ok: false; err: unknown };
          const erpErr = erpFail.err as { error?: { message?: string } } | Error | undefined;
          const erpMsg =
            (erpErr && typeof erpErr === 'object' && 'error' in erpErr
              ? erpErr.error?.message
              : undefined) ||
            (erpErr instanceof Error ? erpErr.message : String(erpFail.err ?? 'ERP'));
          const lcMsg =
            lcFail.err instanceof Error ? lcFail.err.message : String(lcFail.err ?? 'caisse');
          throw new Error(`Création entrepôt impossible (ERP et caisse) : ${erpMsg} ; ${lcMsg}`);
        }
        if (!lc.ok) {
          const lcFail = lc as { ok: false; err: unknown };
          throw lcFail.err instanceof Error ? lcFail.err : new Error(String(lcFail.err));
        }

        const lcOk = lc as { ok: true; res: EntrepotApiRecord };
        const lcRecord = { ...lcOk.res };
        if (erp.ok) {
          const erpId = Number(
            (erp.res as { id?: number })?.id ??
              (erp.res as { entrepot?: { id?: number } })?.entrepot?.id
          );
          if (Number.isFinite(erpId) && erpId > 0) {
            lcRecord.erp_id = erpId;
          }
        } else {
          const erpFail = erp as { ok: false; err: unknown };
          console.warn('[createEntrepot] LC OK, ERP non créé', erpFail.err);
        }
        return lcRecord;
      })
    );
  }

  /** POST LC uniquement (usage interne / sync ciblée). */
  createEntrepotLcOnly(body: EntrepotFormPayload): Observable<EntrepotApiRecord> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot`;
    return this.http.post<EntrepotMutationResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success || res.entrepot == null) {
          throw new Error(res?.error || 'Création impossible');
        }
        this.clearEntrepotOptionsCache();
        const e = res.entrepot as unknown as EntrepotApiRecord;
        if (e.id == null || Number.isNaN(Number(e.id))) {
          throw new Error('Réponse entrepôt invalide (id manquant).');
        }
        return e;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la création.'))
      )
    );
  }

  /** GET /api/entrepots/entrepot/id/:id — fiche entrepôt LC (JWT). */
  getEntrepotById(entrepotId: number): Observable<EntrepotApiRecord> {
    const id = Math.trunc(Number(entrepotId));
    if (!Number.isFinite(id) || id <= 0) {
      return throwError(() => new Error('Identifiant entrepôt invalide.'));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/id/${id}`;
    return this.http.get<EntrepotMutationResponse>(url).pipe(
      map((res) => {
        if (!res?.success || res.entrepot == null) {
          throw new Error(res?.error || 'Entrepôt introuvable');
        }
        const e = res.entrepot as unknown as EntrepotApiRecord;
        if (e.id == null || Number.isNaN(Number(e.id))) {
          throw new Error('Réponse entrepôt invalide (id manquant).');
        }
        return e;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement de l\'entrepôt.'))
      )
    );
  }

  /** PUT /api/entrepots/entrepot/:id */
  updateEntrepot(id: string | number, body: EntrepotFormPayload): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${encodeURIComponent(String(id))}`;
    return this.http.put<EntrepotMutationResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Mise à jour impossible');
        }
        this.clearEntrepotOptionsCache();
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour.'))
      )
    );
  }

  /** GET /api/entrepots/entrepot-caisses/station/:station_id */
  listStationCaissesForAssociation(): Observable<EntrepotCaisseOption[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot-caisses/station/${stationId}`;
    return this.http.get<{ success: boolean; caisses?: EntrepotCaisseOption[] }>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.caisses)) {
          throw new Error('Réponse caisses invalide');
        }
        return res.caisses.map((c) => ({
          ...c,
          label: this.formatCaisseOptionLabel(c),
        }));
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des caisses.'))
      )
    );
  }

  /** PUT /api/entrepots/entrepot/:id/caisses */
  syncEntrepotCaisses(entrepotId: number, caisseIds: number[]): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${entrepotId}/caisses`;
    return this.http.put<{ success: boolean }>(url, { caisse_ids: caisseIds }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error('Enregistrement impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de l\'association caisse / entrepôt.'))
      )
    );
  }

  formatCaisseOptionLabel(c: EntrepotCaisseOption): string {
    const num = c.numero_de_caisse != null ? `n° ${c.numero_de_caisse}` : `#${c.id}`;
    const wh = c.entrepot_libelle?.trim() || '';
    const configured = c.has_uuid ? ' — configurée' : ' — à initialiser';
    return wh ? `Caisse ${num} (${wh})${configured}` : `Caisse ${num}${configured}`;
  }

  /**
   * Id entrepôt LC pour POST entrée stock (BR) : hidden LC puis mapping ERP → LC.
   */
  resolveLcEntrepotIdForStockEntry(options: {
    lcHiddenId: number | null;
    selectedEntrepotId?: number | null;
    matchErpEntrepot: (erpId: number) => Observable<Record<string, unknown> | null>;
  }): Observable<number> {
    const hidden = options.lcHiddenId;
    if (hidden != null && Number.isFinite(hidden) && hidden > 0) {
      return of(hidden);
    }
    const erpId = Number(options.selectedEntrepotId);
    if (!Number.isFinite(erpId) || erpId <= 0) {
      return throwError(
        () => new Error('Entrepôt logiciel caisse introuvable pour ce bon de réception.')
      );
    }
    return options.matchErpEntrepot(erpId).pipe(
      switchMap((detail) => {
        const fromDetail = [
          (detail as any)?.logiciel_caisse_entrepot_id,
          (detail as any)?.lc_entrepot_id,
          (detail as any)?.entrepot_lc_id,
        ]
          .map((v) => Number(v))
          .find((n) => Number.isFinite(n) && n > 0);
        if (fromDetail) {
          return of(fromDetail);
        }
        return this.listForDocumentSelectFromLcSession().pipe(
          switchMap((rows) => {
            const erpMatch = (rows || []).find(
              (w) => Number((w as any).erp_id) === erpId || Number(w.id) === erpId
            );
            const lcId = Number(erpMatch?.id);
            if (Number.isFinite(lcId) && lcId > 0) {
              return of(lcId);
            }
            return throwError(
              () =>
                new Error(
                  `Entrepôt ERP ${erpId} non relié à un entrepôt logiciel caisse pour cette station.`
                )
            );
          })
        );
      })
    );
  }
}
